"use client";

import { useEffect, useRef, useState } from "react";

type AnimatedStatCardProps = {
  label: string;
  value: string;
  unit?: string;
};

function parseNumeric(s: string): { num: string; u: string } {
  const match = s.match(/^([\d:.,]+)\s*(.*)$/);
  return match ? { num: match[1], u: match[2] } : { num: s, u: "" };
}

function isDuration(s: string): boolean {
  return /^\d+:\d{2}(:\d{2})?$/.test(s);
}

function durationToSeconds(s: string): number {
  const parts = s.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return parts[0] * 60 + parts[1];
}

function secondsToDuration(secs: number, format3: boolean): string {
  secs = Math.round(secs);
  if (format3) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function useAnimatedValue(targetStr: string) {
  const [display, setDisplay] = useState("0");
  const rafRef = useRef<number>(0);

  useEffect(() => {
    // Cancel any previous animation
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const duration = 800;
    const startTime = performance.now();

    if (isDuration(targetStr)) {
      const targetSecs = durationToSeconds(targetStr);
      const is3Part = targetStr.split(":").length === 3;

      const animate = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * targetSecs;
        setDisplay(secondsToDuration(current, is3Part));
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setDisplay(targetStr);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    } else {
      const cleaned = targetStr.replace(/,/g, "");
      const targetNum = parseFloat(cleaned);

      if (isNaN(targetNum)) {
        setDisplay(targetStr);
        return;
      }

      const decimals = cleaned.includes(".") ? cleaned.split(".")[1].length : 0;
      const hasCommas = targetStr.includes(",");

      const animate = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * targetNum;
        let formatted = current.toFixed(decimals);
        if (hasCommas) {
          const [int, dec] = formatted.split(".");
          formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (dec ? `.${dec}` : "");
        }
        setDisplay(formatted);
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setDisplay(targetStr);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [targetStr]);

  return display;
}

export function AnimatedStatCard({ label, value, unit }: AnimatedStatCardProps) {
  const parts = unit ? { num: value, u: unit } : parseNumeric(value);
  const animatedNum = useAnimatedValue(parts.num);

  return (
    <div className="bg-muted/50 border border-border rounded-xl p-4 flex flex-col justify-between glass-panel min-w-0 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both">
      <p className="text-xs font-bold text-muted-foreground uppercase">{label}</p>
      <div className="mt-2 min-w-0">
        <span className="text-2xl sm:text-3xl font-black tracking-tight tabular-nums">{animatedNum}</span>
        {parts.u && <span className="text-sm font-medium text-muted-foreground ml-1">{parts.u}</span>}
      </div>
    </div>
  );
}
